import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  Create,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  required,
} from "react-admin";
import CustomDateTimeInput from "shared/components/inputs/CustomDateTimeInput";
import { performanceChronicleResultChoices, performanceTypeChoices } from "shared/enums/reactAdminChoices";
import { SiteResourcesEnum } from "shared/enums/resourceMappings";
import { InputGroupProps, PerformanceChronicleResultErrors, PerformanceErrors, PerformanceValues } from "./types";

const InputGroup = ({ label, source, unit }: InputGroupProps) => (
  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
    <Box width={220} flexShrink={0}>
      {label}
    </Box>
    <Box width={200}>
      <NumberInput
        source={source}
        label=""
        size="small"
        sx={{
          "& .MuiInputBase-root": {
            height: 32,
            minHeight: 0,
            fontSize: 15,
            background: "#f5f5f5",
          },
          "& input": {
            padding: "4px 8px",
          },
        }}
      />
    </Box>
    <Typography color="text.secondary" minWidth={50}>
      {unit}
    </Typography>
  </Stack>
);

const validateRange = (value: number, min: number, max: number): boolean => value >= min && value <= max;

type ChronicleItem = {
  field: keyof PerformanceChronicleResultErrors;
  value: number;
  label: string;
};

function validateGroup(
  items: ChronicleItem[],
  rangeMin: number,
  rangeMax: number,
  requiredMsgPrefix: string,
  rangeMsg: string,
): Array<Record<string, string>> {
  const errs: Array<Record<string, string>> = [];
  let prevValue: number | null = null;

  for (let i = 0; i < items.length; i++) {
    const { field, value, label } = items[i];

    // 1) required
    if (value == null || isNaN(value)) {
      errs.push({ [field]: `${requiredMsgPrefix} ${label} est requis` });
      prevValue = null;
      continue;
    }

    // 2) range
    if (value < rangeMin || value > rangeMax) {
      errs.push({ [field]: rangeMsg });
    }

    // 3) ascending
    if (prevValue != null && value < prevValue) {
      errs.push({
        [field]: `La valeur doit être ≥ ${items[i - 1].label}`,
      });
    }

    prevValue = value;
  }

  return errs;
}

export const validateChronicleResult = (result: Record<string, any>): PerformanceChronicleResultErrors => {
  const errors: PerformanceChronicleResultErrors = {};

  // Power
  const p = result.power_mw;
  if (p == null || isNaN(p)) {
    errors.power_mw = "La puissance est requise";
  } else if (!validateRange(p, 0, 1.25)) {
    errors.power_mw = "La valeur doit être entre 0 et 1.25";
  }

  const socGroup: ChronicleItem[] = [
    { field: "soc_min_pct", value: result.soc_min_pct, label: "SoC min" },
    { field: "soc_clipping_min_pct", value: result.soc_clipping_min_pct, label: "SoC bridage min" },
    { field: "soc_clipping_max_pct", value: result.soc_clipping_max_pct, label: "SoC bridage max" },
    { field: "soc_max_pct", value: result.soc_max_pct, label: "SoC max" },
  ];
  validateGroup(socGroup, 0, 100, "Le", "La valeur doit être entre 0 et 100").forEach((err) => {
    const key = Object.keys(err)[0] as keyof PerformanceChronicleResultErrors;
    errors[key] = err[key] ?? "";
  });

  const injectionGroup: ChronicleItem[] = [
    {
      field: "autonomy_clipping_injection_mwh",
      value: result.autonomy_clipping_injection_mwh,
      label: "SoC bridage max – SoC bridage min",
    },
    {
      field: "autonomy_max_cmin_mwh",
      value: result.autonomy_max_cmin_mwh,
      label: "SoC max – SoC bridage min",
    },
    {
      field: "autonomy_max_min_injection_mwh",
      value: result.autonomy_max_min_injection_mwh,
      label: "SoC max – SoC min",
    },
  ];
  validateGroup(injectionGroup, 0, 5, "L'autonomie", "La valeur doit être entre 0 et 5").forEach((err) => {
    const key = Object.keys(err)[0] as keyof PerformanceChronicleResultErrors;
    errors[key] = err[key] ?? "";
  });

  const withdrawalGroup: ChronicleItem[] = [
    {
      field: "autonomy_clipping_withdrawal_mwh",
      value: result.autonomy_clipping_withdrawal_mwh,
      label: "SoC bridage max – SoC bridage min",
    },
    {
      field: "autonomy_cmax_min_mwh",
      value: result.autonomy_cmax_min_mwh,
      label: "SoC bridage max – SoC min",
    },
    {
      field: "autonomy_max_min_withdrawal_mwh",
      value: result.autonomy_max_min_withdrawal_mwh,
      label: "SoC max – SoC min",
    },
  ];
  validateGroup(withdrawalGroup, 0, 5, "L'autonomie", "La valeur doit être entre 0 et 5").forEach((err) => {
    const key = Object.keys(err)[0] as keyof PerformanceChronicleResultErrors;
    errors[key] = err[key] ?? "";
  });

  return errors;
};

type RequiredPerformanceFields = Pick<
  PerformanceValues,
  "type" | "chronicle_result" | "site_id" | "start_date" | "end_date"
>;

const requiredFields: Record<keyof RequiredPerformanceFields, string> = {
  type: "Le type",
  chronicle_result: "Le résultat du chronique",
  site_id: "Le site",
  start_date: "La date de début",
  end_date: "La date de fin",
};

export const validatePerformance = (values: PerformanceValues): PerformanceErrors => {
  const errors: PerformanceErrors = {};

  (Object.entries(requiredFields) as Array<[keyof RequiredPerformanceFields, string]>).forEach(([field, label]) => {
    if (!values[field]) {
      errors[field] = `${label} est requis`;
    }
  });

  if (!values.performance_chronicle_results?.length) {
    errors.performance_chronicle_results = [];
  } else {
    const chronicleErrors = values.performance_chronicle_results
      .map(validateChronicleResult)
      .filter((err) => Object.keys(err).length > 0);

    if (chronicleErrors.length) {
      errors.performance_chronicle_results = chronicleErrors.map((err) => ({
        ...err,
        _error: true,
      }));
    }
  }

  return errors;
};

const PerformanceCreate = () => {
  const transform = (data: any) => {
    return {
      ...data,
      chronicle_filename: data.type === "msi" ? "fr_msi_jbox" : "fr_performance_jbox",
    };
  };

  return (
    <Create transform={transform}>
      <SimpleForm validate={validatePerformance}>
        <Paper elevation={2} sx={{ p: 3, mb: 3, width: "100%" }}>
          <Stack direction="row" spacing={1}>
            <TextInput
              sx={{ display: "none" }}
              source="chronicle_filename"
              disabled
              defaultValue="fr_performance_jbox"
            />
            <SelectInput source="type" choices={performanceTypeChoices} fullWidth validate={[required()]} />
            <CustomDateTimeInput source="start_date" fullWidth validate={[required()]} />
            <CustomDateTimeInput source="end_date" fullWidth validate={[required()]} />
            <SelectInput
              source="chronicle_result"
              choices={performanceChronicleResultChoices}
              fullWidth
              validate={[required()]}
            />
            <ReferenceInput source="site_id" reference={SiteResourcesEnum.Sites} fullWidth>
              <AutocompleteInput
                validate={[required()]}
                sx={{
                  "& .MuiFormControl-root": {
                    mt: 0,
                  },
                }}
              />
            </ReferenceInput>
          </Stack>
        </Paper>

        <ArrayInput source="performance_chronicle_results">
          <SimpleFormIterator>
            <Box width={"40%"}>
              <InputGroup label={<b>Power (MW)</b>} source="power_mw" unit="MW" />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom textAlign="center" fontWeight="bold">
                  SoC (%)
                </Typography>
                <InputGroup label={<b>SoC min</b>} source="soc_min_pct" unit="%" />
                <InputGroup label="SoC bridage min" source="soc_clipping_min_pct" unit="%" />
                <InputGroup label="SoC bridage max" source="soc_clipping_max_pct" unit="%" />
                <InputGroup label={<b>SoC max</b>} source="soc_max_pct" unit="%" />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom textAlign="center" fontWeight="bold">
                  Autonomies en injection
                </Typography>
                <InputGroup label={<b>SoC max - SoC min</b>} source="autonomy_max_min_injection_mwh" unit="MWh" />
                <InputGroup
                  label={
                    <>
                      <b>SoC max</b> - SoC bridage min
                    </>
                  }
                  source="autonomy_max_cmin_mwh"
                  unit="MWh"
                />
                <InputGroup
                  label="SoC bridage max - SoC bridage min"
                  source="autonomy_clipping_injection_mwh"
                  unit="MWh"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom textAlign="center" fontWeight="bold">
                  Autonomies en soutirage
                </Typography>
                <InputGroup label={<b>SoC max - SoC min</b>} source="autonomy_max_min_withdrawal_mwh" unit="MWh" />
                <InputGroup
                  label={
                    <>
                      SoC bridage max - <b>SoC min</b>
                    </>
                  }
                  source="autonomy_cmax_min_mwh"
                  unit="MWh"
                />
                <InputGroup
                  label="SoC bridage max - SoC bridage min"
                  source="autonomy_clipping_withdrawal_mwh"
                  unit="MWh"
                />
              </Grid>
            </Grid>
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Create>
  );
};

export default PerformanceCreate;
